import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Checklist, ChecklistCategory, LoadingState } from '@types'
import { checklistsApi } from '@api/checklists'

interface ChecklistsState {
  checklists: Checklist[]
  selectedChecklist: Checklist | null
  loadingState: LoadingState
  error: string | null
  checklistEdited: boolean
}

const initialState: ChecklistsState = {
  checklists: [],
  selectedChecklist: null,
  loadingState: 'idle',
  error: null,
  checklistEdited: false,
}

/**
 * The backend (apps.inspections.checklist_serializers) returns each checklist's item tree
 * under `taxonomy` (CategoryNode[]: input_type/is_required/input_options/children), not the
 * legacy `categories` shape (ChecklistCategory[]: input/required/options/children) that
 * ChecklistInput.tsx and Inspect.tsx render. Map one onto the other here, once, so consumers
 * of `state.checklists` can keep reading `.categories` unchanged.
 */
const mapTaxonomyToCategories = (nodes?: any[]): ChecklistCategory[] | undefined => {
  if (!nodes) return undefined
  return nodes.map((node) => ({
    id: node.id,
    name: node.name,
    slug: node.slug,
    description: node.description,
    type: node.type,
    icon: node.icon,
    color: node.color,
    order: node.order,
    parent_id: node.parent_id,
    required: node.is_required,
    input: node.input_type && node.input_type !== 'none' ? node.input_type : undefined,
    options: Array.isArray(node.input_options)
      ? node.input_options.map((opt: any, index: number) =>
          opt && typeof opt === 'object'
            ? { id: opt.id ?? index, name: opt.label ?? opt.name ?? String(opt.value), value: opt.value ?? opt.label ?? opt.name }
            : { id: index, name: String(opt), value: opt }
        )
      : undefined,
    children: mapTaxonomyToCategories(node.children) || [],
    state: node.state,
  }))
}

const withMappedCategories = (checklist: Checklist): Checklist => {
  if (checklist.categories || !checklist.taxonomy) return checklist
  return { ...checklist, categories: mapTaxonomyToCategories(checklist.taxonomy) }
}

// Async thunks
export const fetchChecklists = createAsyncThunk<Checklist[], void>(
  'checklists/fetchChecklists',
  async (_, { rejectWithValue }) => {
    try {
      const response = await checklistsApi.getChecklists()
      // API returns { success, data: { checklists: [], checklist: {} }, message }
      if (response.data && 'checklists' in response.data) {
        return response.data.checklists
      }
      return response.data as unknown as Checklist[]
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch checklists')
    }
  }
)

export const fetchChecklist = createAsyncThunk<Checklist, number>(
  'checklists/fetchChecklist',
  async (id, { rejectWithValue }) => {
    try {
      const response = await checklistsApi.getChecklist(id)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch checklist')
    }
  }
)

export const createChecklist = createAsyncThunk<Checklist, any>(
  'checklists/createChecklist',
  async (checklistData, { rejectWithValue }) => {
    try {
      const response = await checklistsApi.createChecklist(checklistData)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create checklist')
    }
  }
)

export const updateChecklist = createAsyncThunk<Checklist, { id: number; data: any }>(
  'checklists/updateChecklist',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await checklistsApi.updateChecklist(id, data)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update checklist')
    }
  }
)

export const deleteChecklist = createAsyncThunk<number, number>(
  'checklists/deleteChecklist',
  async (id, { rejectWithValue }) => {
    try {
      await checklistsApi.deleteChecklist(id)
      return id
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete checklist')
    }
  }
)

export const duplicateChecklist = createAsyncThunk<Checklist, number>(
  'checklists/duplicateChecklist',
  async (id, { rejectWithValue }) => {
    try {
      const response = await checklistsApi.duplicateChecklist(id)
      // API returns { success, data: { checklist_id, checklist }, message }
      if (response.data && 'checklist' in response.data) {
        return response.data.checklist as unknown as Checklist
      }
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to duplicate checklist')
    }
  }
)

const checklistsSlice = createSlice({
  name: 'checklists',
  initialState,
  reducers: {
    setSelectedChecklist: (state, action: PayloadAction<Checklist | null>) => {
      state.selectedChecklist = action.payload
    },
    setChecklistEdited: (state, action: PayloadAction<boolean>) => {
      state.checklistEdited = action.payload
    },
    updateChecklistCategory: (state, action: PayloadAction<{ categoryId: number; data: Partial<ChecklistCategory> }>) => {
      if (state.selectedChecklist && state.selectedChecklist.categories) {
        const updateCategory = (categories: ChecklistCategory[]): ChecklistCategory[] => {
          return categories.map(category => {
            if (category.id === action.payload.categoryId) {
              return { ...category, ...action.payload.data }
            }
            if (category.children && category.children.length > 0) {
              return { ...category, children: updateCategory(category.children) }
            }
            return category
          })
        }

        state.selectedChecklist.categories = updateCategory(state.selectedChecklist.categories!)
        state.checklistEdited = true
      }
    },
    addChecklistCategory: (state, action: PayloadAction<{ parentId?: number; category: ChecklistCategory }>) => {
      if (state.selectedChecklist) {
        const { parentId, category } = action.payload

        // Initialize categories if not exists
        if (!state.selectedChecklist.categories) {
          state.selectedChecklist.categories = []
        }

        if (parentId) {
          // Add to parent category
          const addToParent = (categories: ChecklistCategory[]): ChecklistCategory[] => {
            return categories.map(cat => {
              if (cat.id === parentId) {
                return { ...cat, children: [...(cat.children || []), category] }
              }
              if (cat.children && cat.children.length > 0) {
                return { ...cat, children: addToParent(cat.children) }
              }
              return cat
            })
          }
          state.selectedChecklist.categories = addToParent(state.selectedChecklist.categories!)
        } else {
          // Add to root level
          state.selectedChecklist.categories.push(category)
        }
        state.checklistEdited = true
      }
    },
    removeChecklistCategory: (state, action: PayloadAction<number>) => {
      if (state.selectedChecklist && state.selectedChecklist.categories) {
        const removeCategory = (categories: ChecklistCategory[]): ChecklistCategory[] => {
          return categories.filter(category => {
            if (category.id === action.payload) {
              return false
            }
            if (category.children && category.children.length > 0) {
              category.children = removeCategory(category.children)
            }
            return true
          })
        }

        state.selectedChecklist.categories = removeCategory(state.selectedChecklist.categories!)
        state.checklistEdited = true
      }
    },
    clearError: (state) => {
      state.error = null
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch Checklists
      .addCase(fetchChecklists.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(fetchChecklists.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.checklists = action.payload.map(withMappedCategories)
      })
      .addCase(fetchChecklists.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.payload as string
      })

      // Fetch Checklist
      .addCase(fetchChecklist.fulfilled, (state, action) => {
        const checklist = withMappedCategories(action.payload as Checklist)
        state.selectedChecklist = checklist
        // Also update in checklists array if it exists
        const index = state.checklists.findIndex(c => c.id === checklist.id)
        if (index !== -1) {
          state.checklists[index] = checklist
        }
      })

      // Create Checklist
      .addCase(createChecklist.fulfilled, (state, action) => {
        const checklist = action.payload as Checklist
        state.checklists.push(checklist)
        state.selectedChecklist = checklist
        state.checklistEdited = false
      })

      // Update Checklist
      .addCase(updateChecklist.fulfilled, (state, action) => {
        const checklist = action.payload as Checklist
        const index = state.checklists.findIndex(c => c.id === checklist.id)
        if (index !== -1) {
          state.checklists[index] = checklist
        }
        if (state.selectedChecklist?.id === checklist.id) {
          state.selectedChecklist = checklist
        }
        state.checklistEdited = false
      })

      // Delete Checklist
      .addCase(deleteChecklist.fulfilled, (state, action) => {
        state.checklists = state.checklists.filter(c => c.id !== action.payload)
        if (state.selectedChecklist?.id === action.payload) {
          state.selectedChecklist = null
        }
      })

      // Duplicate Checklist
      .addCase(duplicateChecklist.fulfilled, (state, action) => {
        const checklist = action.payload as Checklist
        state.checklists.push(checklist)
      })
  },
})

export const {
  setSelectedChecklist,
  setChecklistEdited,
  updateChecklistCategory,
  addChecklistCategory,
  removeChecklistCategory,
  clearError,
  resetState,
} = checklistsSlice.actions

export default checklistsSlice.reducer
